'use client';

import React, { HTMLAttributes, useState } from 'react';

import parser from 'html-react-parser';
import { cn, getTranslation } from '@/lib/utils';
import { parseOptions } from '@/utils/parseOptions';
import { SingleCarouselCard } from '../SingleCarouselCard';
import './styles.css';
import Slider from 'react-slick';
import { useLocale } from '@/hooks/useLocale';

type GuideItemWithHasPage = GuideItem & { hasPage: boolean };

type SectionData = Section & {
  guideItems?: GuideItemWithHasPage[];
};

interface ISingleCarouselSectionProps extends HTMLAttributes<HTMLDivElement> {
  regionSlug: string;
  sectionData: SectionData;
  showPreview?: boolean;
  discoverMore: string;
}

const discoverSicilyUrl = {
  en: '/sicily/discover-sicily',
  it: '/it/sicilia/scopri-la-sicilia',
  de: '/de/sizilien/sizilien-entdecken',
  fr: '/fr/sicile/decouvrez-la-sicile',
};

const discoverPugliaUrl = {
  en: '/puglia/discover-puglia',
  it: '/it/puglia/scopri-la-puglia',
  de: '/de/apulien/apulien-entdecken',
  fr: '/fr/pouilles/decouvrez-les-pouilles',
};

export const SingleCarouselSection: React.FC<ISingleCarouselSectionProps> = ({
  sectionData,
  discoverMore,
  showPreview,
  regionSlug,
  ...rest
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const lang = useLocale();

  const items = (sectionData.guideItems || []).map(
    (item: GuideItemWithHasPage, index) => {
      const itemInfo = item.surrounding?.id ? item.surrounding : item;
      const translate =
        (itemInfo as GuideItem).guideItemTranslation ||
        (itemInfo as Surrounding).translate;

      const translation = getTranslation(translate || [], lang);

      return (
        <SingleCarouselCard
          className={`group mx-auto ${currentPage === index && 'is-focused'} ${showPreview && 'show-preview desktop:h-[500px] desktop:before:hidden'}`}
          info={{
            banner: `${process.env.NEXT_PUBLIC_S3_URI}/assets/images/placeholder.jpg`,
            body: translation?.description || '',
            title: translation?.title || '',
            url: `${regionSlug === 'sicily' ? discoverSicilyUrl[lang] : discoverPugliaUrl[lang]}/${translation?.slug || ''}`,
            buttonText: item.hasPage ? discoverMore : '',
          }}
          bannerMedia={itemInfo.image}
          lang={lang}
          guideItem={item}
          key={item.id}
          sizes="(min-width: 1024px) 50vw, (min-width: 1200px) 75vw, 900px"
        />
      );
    },
  );

  const settings = {
    dots: true,
    dotsClass: 'carousel-dots',
    infinite: items.length > 1,
    speed: 1000,
    ...(showPreview && items.length > 1
      ? {
          centerPadding: '13.104%',
          centerMode: true,
        }
      : {}),
    arrows: items.length > 1,
    initialSlide: 0,
    slidesToShow: 1,
    beforeChange: (currentSlide: number, nextSlide: number) => {
      setCurrentPage(nextSlide);
    },
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          centerMode: false,
          centerPadding: '0',
          infinite: items.length > 3,
          slidesToShow: items.length >= 3 ? 3 : items.length,
        },
      },
      {
        breakpoint: 1100,
        settings: {
          centerMode: false,
          centerPadding: '0',
          infinite: items.length > 2,
          slidesToShow: items.length >= 2 ? 2 : items.length,
          slidesToScroll: items.length >= 2 ? 2 : items.length,
        },
      },
      {
        breakpoint: 750,
        settings: {
          centerMode: false,
          centerPadding: '0',
          infinite: items.length > 1,
          slidesToShow: 1,
        },
      },
    ],
    slidesToScroll: 1,
  };

  const translation = getTranslation(
    sectionData.guideSectionTranslation || [],
    lang,
  );

  return (
    <section
      className="flex flex-col px-4 max-w-[1680px] pt-[40px] mx-auto mb-[50px] tablet:mb-[80px] w-full min-w-0 tablet:px-10 desktop:px-20 z-10 items-center overflow-x-hidden"
      {...rest}>
      <div className=" w-full px-0 flex flex-col justify-center mb-[60px]">
        <h2 className="mb-4 text-title font-title font-bold text-center">
          {translation.title}
        </h2>
        <div className="text-gray-850 text-lg leading-[1.5] text-center">
          {parser(translation?.description || '', parseOptions)}
        </div>
      </div>
      <div className="w-full min-w-0 desktop:pb-8">
        <Slider
          {...settings}
          className={cn('tour-carousel single-carousel w-full', {
            show_preview: showPreview,
          })}>
          {items}
        </Slider>
      </div>
    </section>
  );
};

export default SingleCarouselSection;
